/**
 * Column definitions for the ambassador table.
 *
 * The same shape as `features/events/table.ts` and `features/leads/table.ts`,
 * deliberately — the three render through the same component, and keeping the
 * module layout identical means a change to one is obvious to apply to the rest.
 *
 * The Ambassador column is not in here: it is always shown, always first, and is
 * the only one that renders two lines, so it has no width entry and cannot be
 * switched off.
 *
 * ## The columns are the design's, not the old table's
 *
 * The hand-written table this replaces showed Name · Where · Status · Instagram
 * · Followers · Campaign code · Contact. The design asks for Ambassador · Status
 * · Compensation · Campaign code · Content delivered · Followers — a programme
 * record, not a roster. Porting the old set would have migrated the wrong
 * columns faithfully, so `DEFAULT_COLUMNS` is the design's six and what the old
 * table showed lives on in the picker, one tick away.
 *
 * Contact is the one thing that did not survive as it was. It was four
 * pass/fail chips — email, phone, address, size — and the shared `Cell` renders
 * a pill or a line of text, nothing else. What it was really saying is that you
 * cannot post merch to somebody without an address, so it comes back as
 * `missing`: the gaps, as a line of text, off by default.
 */
import type {
  Cell as SharedCell,
  ColumnFilters as SharedColumnFilters,
  ColumnMeta,
} from '@/shared/components/DataTable'
import { AMBASSADOR_STATUSES, type Person } from '@/types/events'
import {
  ACCESS_LABEL,
  accessState,
  accessText,
  type AmbassadorAccess,
} from './repository/ambassadorAccess'

export type ColumnKey =
  | 'status'
  | 'premium'
  | 'compensation'
  | 'campaignCode'
  | 'content'
  | 'followers'
  | 'country'
  | 'where'
  | 'instagram'
  | 'missing'

export const COLUMN_META: Record<ColumnKey, ColumnMeta> = {
  status: { label: 'Status', width: 0.8 },
  /** Skida Premium from RevenueCat, and when a grant ends — 27 Oct 2026. */
  premium: { label: 'Premium', width: 1.4 },
  /** Free text — product, fee, affiliate, none. It varies per deal. */
  compensation: { label: 'Compensation', width: 1.2 },
  campaignCode: { label: 'Campaign code', width: 1 },
  /** What they have actually delivered, in their own terms. */
  content: { label: 'Content delivered', width: 1.6 },
  followers: { label: 'Followers', width: 0.9 },
  country: { label: 'Country', width: 1 },
  /** Their own words for where they ski. Not a residence — see the importer. */
  where: { label: 'Where', width: 1.2 },
  instagram: { label: 'Instagram', width: 1.1 },
  /** What is stopping a parcel going out: no address, no size, no way to write. */
  missing: { label: 'Missing', width: 1.2 },
}

export const COLUMN_KEYS = Object.keys(COLUMN_META) as ColumnKey[]

/** The design's set, in the design's order. */
export const DEFAULT_COLUMNS: ColumnKey[] = [
  'status',
  'premium',
  'compensation',
  'campaignCode',
  'content',
  'followers',
]

export type SortKey = ColumnKey | 'name'

/** Followers is a count; the useful first look is the largest, not the smallest. */
export const DESCENDING_FIRST: readonly string[] = ['followers']

/**
 * Columns whose header carries a multi-select filter.
 *
 * Status, compensation and country — the three with a small set of repeated
 * values. Compensation is free text but written from a short vocabulary in
 * practice, so the popover groups it usefully; campaign code and content are
 * near-unique per person and a filter listing every row is a filter nobody uses.
 */
export const FILTERABLE_COLUMNS: ColumnKey[] = ['status', 'premium', 'compensation', 'country']

export type ColumnFilters = SharedColumnFilters<ColumnKey>
export type Cell = SharedCell<ColumnKey>

/**
 * The values a filter compares against, for one person and one column.
 *
 * Plural, unlike the events version: an ambassador can cover several countries,
 * and one who covers Norway and Sweden has to appear under both. Returning a
 * joined string would have offered "Norway, Sweden" as its own option and
 * matched neither filter.
 */
export function filterValues(p: Person, key: ColumnKey, access?: AmbassadorAccess): string[] {
  switch (key) {
    case 'status':
      return [p.status]
    case 'premium':
      return [ACCESS_LABEL[accessState(access)]]
    case 'compensation':
      return [p.compensation || '—']
    case 'country':
      return p.countries.filter(Boolean)
    default:
      return []
  }
}

export function passesFilters(p: Person, filters: ColumnFilters, access?: AmbassadorAccess): boolean {
  return FILTERABLE_COLUMNS.every((key) => {
    const selected = filters[key]
    if (!selected || selected.length === 0) return true
    const values = filterValues(p, key, access)
    return selected.some((v) => values.includes(v))
  })
}

const styleFor = (s: Person['status']) =>
  AMBASSADOR_STATUSES.find((x) => x.key === s) ?? AMBASSADOR_STATUSES[0]!

/** Which of the four things a parcel and a conversation need are not on file. */
export function missingFields(p: Person): string[] {
  return [
    !p.email && 'email',
    !p.phone && 'phone',
    !p.postalAddress && 'address',
    !p.tshirt && 'size',
  ].filter((v): v is string => Boolean(v))
}

/**
 * The follower count, or why there is not one.
 *
 * A blank means two different things and the difference matters: nothing has
 * been fetched yet on a professional account, and nothing ever will be on a
 * personal one — Instagram's Business Discovery only reads professional
 * accounts. So the personal case is a stated answer rather than an empty field.
 */
function followerCell(p: Person): Cell {
  if (p.instagramFollowers !== null) {
    return { key: 'followers', badge: false, text: p.instagramFollowers.toLocaleString('nb-NO') }
  }
  if (p.igAccountType === 'personal') {
    return {
      key: 'followers',
      badge: false,
      text: 'Not available',
      color: 'var(--color-grey)',
      title: 'Instagram only reports follower counts for professional accounts',
    }
  }
  return {
    key: 'followers',
    badge: false,
    text: '—',
    title: p.instagram ? 'Not fetched yet' : 'No Instagram handle on file',
  }
}

/** Green while it runs, amber in its last month, red when there is none. */
const ACCESS_COLOR: Record<ReturnType<typeof accessState>, string | undefined> = {
  active: 'var(--color-success-text)',
  expiring: 'var(--color-warning-text)',
  none: 'var(--color-error-text)',
  'no-account': 'var(--color-grey)',
  unknown: 'var(--color-grey)',
}

export function buildCells(p: Person, columns: ColumnKey[], access?: AmbassadorAccess): Cell[] {
  return columns.map((key): Cell => {
    switch (key) {
      case 'premium': {
        const st = accessState(access)
        return {
          key,
          badge: false,
          text: accessText(access),
          color: ACCESS_COLOR[st],
          title:
            st === 'no-account'
              ? 'No app account has this email. Open the ambassador to type the uid.'
              : st === 'unknown'
                ? 'Not looked up yet'
                : 'From RevenueCat, checked every morning',
        }
      }
      case 'status': {
        const st = styleFor(p.status)
        return { key, badge: true, text: st.label, bg: st.bg, color: st.color }
      }
      case 'compensation':
        return { key, badge: false, text: p.compensation || '—' }
      case 'campaignCode':
        return { key, badge: false, text: p.campaignCode || '—' }
      case 'content':
        return { key, badge: false, text: p.contentDelivered || '—' }
      case 'followers':
        return followerCell(p)
      case 'country':
        return { key, badge: false, text: p.countries.join(', ') || '—' }
      case 'where':
        return { key, badge: false, text: p.homeArea || '—' }
      case 'instagram':
        return { key, badge: false, text: p.instagram ? `@${p.instagram}` : '—' }
      case 'missing': {
        const gaps = missingFields(p)
        return {
          key,
          badge: false,
          text: gaps.join(' · ') || 'nothing',
          color: gaps.length ? 'var(--color-error-text)' : undefined,
          title: gaps.length ? 'Not on file yet' : 'Contact details and size are all on file',
        }
      }
    }
  })
}

/**
 * Sort key for one ambassador.
 *
 * Status sorts by programme position rather than alphabetically. Blanks return
 * null and are pushed to the bottom whichever way the column is sorted — an
 * ambassador with no follower count is not the smallest account, and one with no
 * campaign code is not first in the alphabet.
 */
export function sortValue(p: Person, sortBy: SortKey, access?: AmbassadorAccess): string | number | null {
  switch (sortBy) {
    case 'premium': {
      // Soonest end first ascending; none before any, lifetime last.
      const st = accessState(access)
      if (st === 'unknown') return null
      if (st === 'no-account') return -2
      if (st === 'none') return -1
      return access!.untilMs ?? Number.MAX_SAFE_INTEGER
    }
    case 'status':
      return AMBASSADOR_STATUSES.findIndex((s) => s.key === p.status)
    case 'compensation':
      return p.compensation.toLowerCase() || null
    case 'campaignCode':
      return p.campaignCode.toLowerCase() || null
    case 'content':
      return p.contentDelivered.toLowerCase() || null
    case 'followers':
      return p.instagramFollowers
    case 'country':
      return p.countries.join(', ').toLowerCase() || null
    case 'where':
      return p.homeArea.toLowerCase() || null
    case 'instagram':
      return p.instagram.toLowerCase() || null
    case 'missing':
      // Most gaps first when descending: the ones you cannot send anything to.
      return missingFields(p).length || null
    default:
      return p.name.toLowerCase()
  }
}

export function sortPeople(
  rows: Person[],
  sortBy: SortKey,
  dir: 'asc' | 'desc',
  access: Map<string, AmbassadorAccess> = new Map(),
): Person[] {
  const sign = dir === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => {
    const av = sortValue(a, sortBy, access.get(a.id))
    const bv = sortValue(b, sortBy, access.get(b.id))
    if (av === null && bv === null) return a.name.localeCompare(b.name)
    if (av === null) return 1
    if (bv === null) return -1
    if (av < bv) return -sign
    if (av > bv) return sign
    // Name is the tiebreaker, so equal counts do not shuffle on re-render.
    return a.name.localeCompare(b.name)
  })
}


/**
 * Reach for a card, where there is no column heading to borrow.
 *
 * Decision 41: a "Combined reach" KPI above a list where no row shows reach is
 * the KPI contradicting its own list. So the card carries it — labelled, since
 * a bare "42K" on a card could be anything, and keeping the personal-account
 * state rather than rendering an empty half-line.
 */
export function reachLabel(p: Person): string {
  if (p.instagramFollowers !== null) {
    const n = p.instagramFollowers
    const short =
      n >= 1_000_000
        ? `${(n / 1_000_000).toFixed(1).replace('.', ',')}M`
        : n >= 1000
          ? `${Math.round(n / 1000)}K`
          : String(n)
    return `${short} reach`
  }
  return p.igAccountType === 'personal' ? 'Reach not available' : ''
}
